import { useQuery } from '@tanstack/react-query';
import { Route, Routes, useLocation } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError, type Me } from './api.ts';
import { Shell } from './components/Shell.tsx';
import { PublicShell } from './components/PublicShell.tsx';
import { LoggedOutPage, LoginPage } from './pages/Login.tsx';
import { StartPage } from './pages/Start.tsx';
import { VehiclesPage } from './pages/Vehicles.tsx';
import { VehiclePage } from './pages/Vehicle.tsx';
import { NewVehiclePage } from './pages/NewVehicle.tsx';
import { PoolsPage } from './pages/Pools.tsx';
import { BookPage } from './pages/Book.tsx';
import { BookingPage } from './pages/Booking.tsx';
import { CalendarPage } from './pages/Calendar.tsx';
import { MessagesPage } from './pages/Messages.tsx';
import { KeysPage } from './pages/Keys.tsx';
import { PoolAdminPage } from './pages/PoolAdmin.tsx';
import { ReportsPage } from './pages/Reports.tsx';
import { AdminHome } from './pages/AdminHome.tsx';
import { ImportsPage } from './pages/Imports.tsx';
import { BillingPage } from './pages/Billing.tsx';
import { ApiClientsPage } from './pages/ApiClients.tsx';
import { IdpPage } from './pages/Idp.tsx';
import { RolesPage } from './pages/Roles.tsx';
import { AuditPage } from './pages/Audit.tsx';
import { FieldsAdminPage } from './pages/FieldsAdmin.tsx';
import { BoardPage } from './pages/Board.tsx';
import { usePage } from './page.ts';

function NotFound() {
  const h1 = usePage(sv.errors.notFound);
  return (
    <div className="narrow">
      <h1 ref={h1} tabIndex={-1}>
        {sv.errors.notFound}
      </h1>
      <a href="/">{sv.errors.backToStart}</a>
    </div>
  );
}

function Failure({ error }: { error: unknown }) {
  const text =
    error instanceof ApiError && error.status === 0 ? sv.errors.network : sv.errors.server;
  return (
    <PublicShell>
      <p className="notice" data-state="bad" role="alert">
        {text}
      </p>
    </PublicShell>
  );
}

function Authed() {
  const location = useLocation();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  if (me.isPending) {
    return (
      <PublicShell>
        <p role="status">{sv.app.loading}</p>
      </PublicShell>
    );
  }
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <LoginPage returnTo={location.pathname + location.search} />;
    }
    return <Failure error={me.error} />;
  }
  return (
    <Shell me={me.data}>
      <Routes>
        <Route path="/" element={<StartPage me={me.data} />} />
        <Route path="/boka" element={<BookPage me={me.data} />} />
        <Route path="/bokning/:id" element={<BookingPage me={me.data} />} />
        <Route path="/kalender" element={<CalendarPage />} />
        <Route path="/fordon" element={<VehiclesPage me={me.data} />} />
        <Route path="/fordon/nytt" element={<NewVehiclePage />} />
        <Route path="/fordon/:id" element={<VehiclePage me={me.data} />} />
        <Route path="/bilpooler" element={<PoolsPage me={me.data} />} />
        <Route path="/bilpooler/:id" element={<PoolAdminPage />} />
        <Route path="/nyckelskap" element={<KeysPage />} />
        <Route path="/rapporter" element={<ReportsPage me={me.data} />} />
        <Route path="/admin" element={<AdminHome me={me.data} />} />
        <Route path="/admin/inlasning" element={<ImportsPage />} />
        <Route path="/admin/debitering" element={<BillingPage />} />
        <Route path="/admin/api" element={<ApiClientsPage />} />
        <Route path="/admin/inloggning" element={<IdpPage />} />
        <Route path="/admin/roller" element={<RolesPage />} />
        <Route path="/admin/logg" element={<AuditPage />} />
        <Route path="/admin/fritextfalt" element={<FieldsAdminPage />} />
        <Route path="/meddelanden" element={<MessagesPage />} />
        <Route path="/tavla" element={<BoardPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/inloggning" element={<LoginPage />} />
      <Route path="/utloggad" element={<LoggedOutPage />} />
      <Route path="*" element={<Authed />} />
    </Routes>
  );
}
